import { __assign, __awaiter, __extends, __generator } from "tslib";
import { EventEmitter } from 'eventemitter3';
import whenDomReady from 'when-dom-ready';
import elementClass from 'element-class';
import Penpal from 'penpal';
import { Results } from './results';
import { insertCss } from './utils/insertCss';
var FlatfileImporter = /** @class */ (function (_super) {
    __extends(FlatfileImporter, _super);
    function FlatfileImporter(apiKey, options, customer) {
        var _this = _super.call(this) || this;
        _this.$fieldHooks = [];
        _this.$stepHooks = {};
        _this.apiKey = apiKey;
        _this.options = options;
        _this.customer = customer;
        _this.uuid = _this.$generateUuid();
        _this.$ready = new FlatfileImporter.Promise(function (resolve, reject) {
            _this.$resolver = resolve;
            _this.$rejecter = reject;
        });
        whenDomReady(function () {
            _this.initialize();
        });
        return _this;
    }
    /**
     * This will by default always be `https://www.flatfile.io/importer/:key` unless you are
     * an enterprise customer that is self-hosting the application. In which case, this
     * will be the URL of your enterprise installd Flatfile importer index page
     */
    FlatfileImporter.setMountUrl = function (url) {
        this.MOUNT_URL = url;
    };
    /**
     * This allows you to opt into or out of specific versions of the Flatfile SDK
     */
    FlatfileImporter.setVersion = function (version) {
        switch (version) {
            case 1:
                this.MOUNT_URL = 'https://kiosk-lite.flatfile.io/?key=:key';
                break;
            case 2:
                this.MOUNT_URL = 'https://portal-2.flatfile.io/?key=:key';
                break;
            default:
                throw new Error(version + " is not a valid version");
        }
    };
    /**
     * Call open() to activate the importer overlay dialog.
     */
    FlatfileImporter.prototype.open = function (options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        if (this.handshake === null) {
            throw new Error('This importer has been destroyed.');
        }
        options = __assign(__assign({}, options), { bulkInit: true, hasVirtualRecordHook: !!this.$virtualRecordHook, hasRecordHook: !!(this.$recordHook || this.$bulkInitRecordHook), hasInteractionEventCallback: !!this.$interactionEventCallback, stepHooks: Object.keys(this.$stepHooks), fieldHooks: this.$fieldHooks.map(function (v) { return v.field; }), endUser: this.customer });
        this.$ready.then(function (child) {
            elementClass(document.body).add('flatfile-active');
            var el = document.getElementById("flatfile-" + _this.uuid);
            if (el) {
                el.style.display = 'block';
            }
            child.open(options);
        });
    };
    /**
     * Use load() when you want a promise returned. This is necessary if you want to use
     * async/await for an es6 implementation
     * @deprecated
     */
    FlatfileImporter.prototype.load = function () {
        var _this = this;
        return new FlatfileImporter.Promise(function (resolve, reject) {
            _this.open();
            var cleanup = function () {
                _this.removeListener('close', loadRejectHandler);
                _this.removeListener('complete', loadResolveHandler);
            };
            function loadResolveHandler(rows) {
                resolve(rows);
                cleanup();
            }
            function loadRejectHandler(err) {
                reject(err);
                cleanup();
            }
            _this.on('close', loadRejectHandler);
            _this.on('complete', loadResolveHandler);
        });
    };
    /**
     * Use requestDataFromUser() when you want a promise returned. This is necessary if you want to use
     * async/await for an es6 implementation
     */
    FlatfileImporter.prototype.requestDataFromUser = function (options) {
        if (options === void 0) { options = {}; }
        this.open(__assign(__assign({}, options), { inChunks: options.inChunks || null, expectsExpandedResults: true }));
        return this.responsePromise();
    };
    /**
     * This will display a progress indicator inside the importer if you anticipate that handling
     * the output of the importer may take some time.
     */
    FlatfileImporter.prototype.displayLoader = function (msg) {
        this.$ready.then(function (child) {
            child.displayLoader(msg);
        });
    };
    /**
     * This will display a dialog inside of the importer with an error icon and the message you
     * pass. The user will be able to acknowledge the error and be returned to the import data
     * spreadsheet to ideally fix any issues or attempt submitting again.
     */
    FlatfileImporter.prototype.displayError = function (msg) {
        this.$ready.then(function (child) {
            child.displayError(msg);
        });
    };
    /**
     * This will display a dialog inside of the importer with an error icon and the message you
     * pass. The user will be able to acknowledge the error and be returned to the import data
     * spreadsheet to ideally fix any issues or attempt submitting again.
     *
     * @param corrections - allows user to do server-side validation and provide error / warning
     * messages or value overrides
     */
    FlatfileImporter.prototype.requestCorrectionsFromUser = function (msg, corrections) {
        this.$ready.then(function (child) {
            child.displayError(msg, corrections);
        });
        return this.responsePromise();
    };
    /**
     * This will display a dialog inside of the importer with a success icon and the message you
     * pass.
     *
     * @return Promise that will be resolved when user closes the dialog.
     */
    FlatfileImporter.prototype.displaySuccess = function (msg) {
        var _this = this;
        this.$ready.then(function (child) {
            child.displaySuccess(msg);
        });
        return new Promise(function (resolve) {
            var handleSuccess = function () {
                resolve();
                _this.removeListener('close', handleSuccess);
            };
            _this.on('close', handleSuccess);
        });
    };
    /**
     * Set the customer information for this import
     */
    FlatfileImporter.prototype.setCustomer = function (customer) {
        this.customer = customer;
    };
    /**
     * Set the language for the Portal
     */
    FlatfileImporter.prototype.setLanguage = function (lang) {
        this.$ready.then(function (child) {
            child.setLanguage(lang);
        });
    };
    FlatfileImporter.prototype.addVirtualField = function (field, options) {
        if (options === void 0) { options = {}; }
        this.$ready.then(function (child) {
            child.addVirtualField({ field: field, options: options });
        });
    };
    /**
     * Set the customer information for this import
     */
    FlatfileImporter.prototype.registerRecordHook = function (callback) {
        this.$recordHook = callback;
    };
    FlatfileImporter.prototype.registerBulkInitRecordHook = function (callback) {
        this.$bulkInitRecordHook = callback;
    };
    FlatfileImporter.prototype.registerVirtualRecordHook = function (callback) {
        this.$virtualRecordHook = callback;
    };
    FlatfileImporter.prototype.registerNetworkErrorCallback = function (callback) {
        this.$networkErrorCallback = callback;
    };
    FlatfileImporter.prototype.registerBeforeFetchCallback = function (callback) {
        this.$beforeFetchCallback = callback;
    };
    FlatfileImporter.prototype.registerInteractionEventCallback = function (callback) {
        this.$interactionEventCallback = callback;
    };
    FlatfileImporter.prototype.registerFieldHook = function (field, cb) {
        this.$fieldHooks.push({ field: field, cb: cb });
    };
    FlatfileImporter.prototype.registerStepHook = function (step, callback) {
        this.$stepHooks[step] = callback;
    };
    /**
     * Call close() from the parent window in order to hide the importer. You can do this after
     * handling the import callback so your users don't have to click the confirmation button
     */
    FlatfileImporter.prototype.close = function () {
        this.$ready.then(function (child) {
            child.close();
        });
    };
    /**
     * This will remove the importer's HTML element and will destroy the connection.
     * You wouldn't be able to open an importer if this method gets called.
     */
    FlatfileImporter.prototype.destroy = function () {
        var _a;
        (_a = document.getElementById("flatfile-" + this.uuid)) === null || _a === void 0 ? void 0 : _a.remove();
        this.handshake = null;
    };
    FlatfileImporter.prototype.handleClose = function () {
        elementClass(document.body).remove('flatfile-active');
        var el = document.getElementById("flatfile-" + this.uuid);
        if (el) {
            el.style.display = 'none';
        }
    };
    FlatfileImporter.prototype.initialize = function () {
        var _this = this;
        insertCss("\n      .flatfile-component {\n        position: fixed;\n        top: 0;\n        bottom: 0;\n        right: 0;\n        left: 0;\n        display: none;\n        z-index: 100000;\n      }\n      .flatfile-component iframe {\n        width: 100%;\n        height: 100%;\n        position: absolute;\n        border-width: 0;\n      }\n      body.flatfile-active {\n        overflow: hidden;\n        overscroll-behavior-x: none;\n      }\n    ");
        document.body.insertAdjacentHTML('beforeend', "<div id=\"flatfile-" + this.uuid + "\" class=\"flatfile-component\"></div>");
        var timeout = setTimeout(function () {
            return console.error('[Flatfile] Looks like Portal takes too long to load. Please visit our Help Center (https://support.flatfile.com/hc/en-us/articles/4408071883924-CORS-Referrer-Policy-Recommendations) or contact Flatfile support for any help.');
        }, 5000);
        this.handshake = Penpal.connectToChild({
            appendTo: document.getElementById("flatfile-" + this.uuid) || undefined,
            url: FlatfileImporter.MOUNT_URL.replace(':key', this.apiKey),
            methods: {
                results: function (data) {
                    _this.emit('results', data.results, data.meta);
                },
                complete: function (data) {
                    _this.emit('complete', data.rows, data.meta);
                },
                close: function () {
                    _this.emit('close');
                    _this.handleClose();
                },
                networkErrorCallback: function (error) {
                    return _this.$networkErrorCallback ? _this.$networkErrorCallback(error) : undefined;
                },
                beforeFetchCallback: function (req) {
                    return _this.$beforeFetchCallback ? _this.$beforeFetchCallback(req) : undefined;
                },
                interactionEventCallback: function (req) {
                    return _this.$interactionEventCallback ? _this.$interactionEventCallback(req) : undefined;
                },
                dataHookCallback: function (row, index, mode) {
                    try {
                        return _this.$recordHook ? _this.$recordHook(row, index, mode) : undefined;
                    }
                    catch (_a) {
                        var message = _a.message, stack = _a.stack;
                        console.error("Flatfile Record Hook Error on row " + index + ":\n  " + stack, { row: row, mode: mode });
                        return {};
                    }
                },
                bulkHookCallback: function (rows, mode) {
                    if (_this.$bulkInitRecordHook) {
                        try {
                            return _this.$bulkInitRecordHook(rows);
                        }
                        catch (_a) {
                            var stack = _a.stack;
                            console.error("Flatfile Bulk Init Record Hook Error:\n  " + stack, { rows: rows });
                            return {};
                        }
                    }
                    try {
                        if (mode === 'virtual') {
                            return _this.$virtualRecordHook
                                ? Promise.all(rows.map(function (_a) {
                                    var row = _a[0], index = _a[1];
                                    try {
                                        return _this.$virtualRecordHook(row, index, mode);
                                    }
                                    catch (e) {
                                        e.row = row;
                                        e.index = index;
                                        throw e;
                                    }
                                }))
                                : undefined;
                        }
                        return _this.$recordHook
                            ? Promise.all(rows.map(function (_a) {
                                var row = _a[0], index = _a[1];
                                try {
                                    return _this.$recordHook(row, index, mode);
                                }
                                catch (e) {
                                    e.row = row;
                                    e.index = index;
                                    throw e;
                                }
                            }))
                            : undefined;
                    }
                    catch (_b) {
                        var stack = _b.stack, row = _b.row, index = _b.index;
                        console.error("Flatfile Record Hook Error on row " + index + ":\n  " + stack, { row: row, mode: mode });
                        return {};
                    }
                },
                fieldHookCallback: function (values, meta) {
                    var fieldHook = _this.$fieldHooks.find(function (v) { return v.field === meta.field; });
                    if (!fieldHook) {
                        return undefined;
                    }
                    try {
                        return fieldHook.cb(values, meta);
                    }
                    catch (_a) {
                        var stack = _a.stack;
                        console.error("Flatfile Field Hook Error on field \"" + meta.field + "\":\n  " + stack, {
                            meta: meta,
                            values: values
                        });
                        return [];
                    }
                },
                stepHookCallback: function (step, payload) { return __awaiter(_this, void 0, void 0, function () {
                    var _a, stack;
                    return __generator(this, function (_b) {
                        switch (_b.label) {
                            case 0:
                                if (!this.$stepHooks[step]) {
                                    return [2 /*return*/, undefined];
                                }
                                _b.label = 1;
                            case 1:
                                _b.trys.push([1, 3, , 4]);
                                return [4 /*yield*/, this.$stepHooks[step](payload)];
                            case 2: return [2 /*return*/, _b.sent()];
                            case 3:
                                _a = _b.sent();
                                stack = _a.stack;
                                console.error("Flatfile Step Hook Error on step \"" + step + "\":\n  " + stack, {
                                    payload: payload
                                });
                                return [3 /*break*/, 4];
                            case 4: return [2 /*return*/];
                        }
                    });
                }); },
                ready: function () {
                    _this.handshake.promise
                        .then(function (child) {
                        _this.$resolver(child);
                        if (_this.customer) {
                            child.setUser(_this.customer);
                        }
                    })
                        .catch(function (err) {
                        console.error(err);
                    });
                    return _this.options;
                }
            }
        });
        this.handshake.promise.then(function () {
            if (timeout)
                clearTimeout(timeout);
        });
        this.handshake.promise.catch(function (err) {
            _this.$rejecter(err);
        });
    };
    FlatfileImporter.prototype.$generateUuid = function () {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    };
    FlatfileImporter.prototype.responsePromise = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var loadResolveHandler = function (rows, meta) { return __awaiter(_this, void 0, void 0, function () {
                var results;
                return __generator(this, function (_a) {
                    results = new Results(rows, meta, this);
                    resolve(results);
                    cleanup();
                    return [2 /*return*/];
                });
            }); };
            function loadRejectHandler(err) {
                reject(err);
                cleanup();
            }
            var self = _this;
            function cleanup() {
                self.removeListener('close', loadRejectHandler);
                self.removeListener('results', loadResolveHandler);
            }
            _this.on('close', loadRejectHandler);
            _this.on('results', loadResolveHandler);
        });
    };
    FlatfileImporter.Promise = Promise;
    FlatfileImporter.MOUNT_URL = 'https://portal-2.flatfile.io/?key=:key';
    return FlatfileImporter;
}(EventEmitter));
export { FlatfileImporter };
//# sourceMappingURL=data:application/json;base64,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